import { useEffect, useRef, useMemo } from 'react';

import type { BaseField, FieldDescriptor, StaticFieldProps } from '../../types/common';

export type HiddenFieldType = BaseField & {
  type: 'HiddenField';
  goToNextSection?: string;
  defaultValue?: unknown;
  resetValue?: string | number | null | undefined | object;
  /**
   * When true, automatically sets the defaultValue in form state on mount.
   * Use this when dependent fields need this value to be present immediately.
   * Without this flag, HiddenField only stores the value without setting form state.
   */
  setDefaultOnMount?: boolean;
};

function HiddenField({
  onNextSectionClick,
  field,
  onValueChange,
  value,
  isTouched,
}: StaticFieldProps<HiddenFieldType>) {
  const hasResetRef = useRef(false);
  const hasSetDefaultRef = useRef(false);

  const resetValueString = useMemo(
    () => (field?.resetValue !== undefined ? JSON.stringify(field.resetValue) : null),
    [field?.resetValue]
  );

  useEffect(() => {
    if (field?.goToNextSection) {
      onNextSectionClick?.();
    }
  }, [onNextSectionClick, field?.goToNextSection]);

  // Set default value in form state on mount (opt-in behavior)
  // Only runs when setDefaultOnMount: true is explicitly set by the backend
  // This ensures dependent fields can access this value immediately
  useEffect(() => {
    if (!field?.setDefaultOnMount) return;
    if (hasSetDefaultRef.current) return;
    if (value !== undefined) return;
    if (field?.defaultValue === undefined) return;
    if (isTouched) return;

    hasSetDefaultRef.current = true;
    onValueChange?.(field.defaultValue, true); // skipTouched = true
  }, [field?.setDefaultOnMount, field?.defaultValue, value, onValueChange, isTouched]);

  useEffect(() => {
    if (resetValueString && onValueChange && !hasResetRef.current) {
      const currentValueString = value ? JSON.stringify(value) : null;

      if (currentValueString !== resetValueString) {
        hasResetRef.current = true;
        onValueChange(field.resetValue);

        setTimeout(() => {
          hasResetRef.current = false;
        }, 0);
      }
    }
  }, [resetValueString, onValueChange, value, field?.resetValue]);

  return null;
}

const Descriptor: FieldDescriptor<HiddenFieldType> = {
  FieldComponent: HiddenField,
  name: 'HiddenField',
  renderIfHidden: true,
  rendersNull: true,
};

export default Descriptor;
